import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, Package, FileText, ClipboardList, TrendingUp, Clock, CheckCircle2, Euro, AlertTriangle, CalendarDays, Wrench, Car } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

function Kpi({ icon: Icon, label, value, testId, accent, to, hint }) {
  const content = (
    <Card className={`h-full p-5 transition ${to ? "hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-slate-400"}`} />
      </div>
      <div className="mt-3 font-heading text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </Card>
  );
  return to ? <Link to={to} className="block">{content}</Link> : content;
}

export default function Dashboard() {
  const { t, fmtCurrency, fmtDate } = useI18n();
  const { activeTenantId, can } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    enabled: !!activeTenantId,
  });

  if (isLoading || !data?.counts) {
    return (
      <div>
        <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
        </div>
      </div>
    );
  }

  const c = data.counts;
  return (
    <div>
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} />

      {data.finance && can("einvoice:view") && <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={FileText} label={t("dashboard.open_invoices")} value={data.finance.open_count} hint={t("dashboard.open_invoices_hint")} to="/finance/einvoices" testId="kpi-open-invoices" accent="text-sky-500" />
        <Kpi icon={Euro} label={t("dashboard.outstanding_invoice_amount")} value={fmtCurrency(data.finance.outstanding_amount)} hint={t("dashboard.outstanding_invoice_hint")} to="/finance/einvoices" testId="kpi-invoice-outstanding" accent="text-amber-500" />
        <Kpi icon={AlertTriangle} label={t("dashboard.overdue_invoices")} value={data.finance.overdue_count} hint={fmtCurrency(data.finance.overdue_amount)} to="/finance/dunning" testId="kpi-overdue-invoices" accent="text-red-500" />
        <Kpi icon={TrendingUp} label={t("dashboard.total_invoiced")} value={fmtCurrency(data.finance.total_invoiced_amount)} hint={t("dashboard.total_invoiced_hint")} to="/finance/einvoices" testId="kpi-total-invoiced" accent="text-emerald-500" />
      </div>}

      {(data.operations || data.automotive) && <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.operations && <Kpi icon={CalendarDays} label={t("dashboard.appointments_today")} value={data.operations.appointments_today} to="/jobs?view=calendar" testId="kpi-appointments-today" accent="text-blue-500" />}
        {data.operations && <Kpi icon={Wrench} label={t("dashboard.ready_to_invoice")} value={data.operations.ready_to_invoice} to="/jobs?status=ready_to_invoice" testId="kpi-ready-to-invoice" accent="text-emerald-500" />}
        {data.operations && <Kpi icon={Clock} label={t("dashboard.jobs_in_progress")} value={data.operations.in_progress} to="/jobs?status=in_progress" testId="kpi-jobs-in-progress" accent="text-violet-500" />}
        {data.automotive && <Kpi icon={Car} label={t("dashboard.vehicles_due")} value={data.automotive.vehicles_due_30_days} hint={t("dashboard.vehicles_due_hint")} to={`/vehicles?hu_from=${data.automotive.due_from}&hu_to=${data.automotive.due_to}`} testId="kpi-vehicles-due" accent="text-orange-500" />}
      </div>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Clock} label={t("dashboard.outstanding_quotes")} value={c.quotes_sent} to="/quotes" testId="kpi-outstanding" accent="text-sky-500" />
        <Kpi icon={CheckCircle2} label={t("dashboard.accepted_quotes")} value={c.quotes_accepted} to="/quotes" testId="kpi-accepted" accent="text-emerald-500" />
        <Kpi icon={FileText} label={t("dashboard.draft_quotes")} value={c.quotes_draft} to="/quotes" testId="kpi-drafts" />
        <Kpi icon={TrendingUp} label={t("dashboard.conversion")} value={`${data.conversion_rate}%`} to="/quotes" testId="kpi-conversion" accent="text-violet-500" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Users} label={t("dashboard.customers")} value={c.customers} to="/customers" testId="kpi-customers" />
        <Kpi icon={Package} label={t("dashboard.products")} value={c.products} to="/products" testId="kpi-products" />
        <Kpi icon={ClipboardList} label={t("dashboard.orders")} value={c.orders} to="/orders" testId="kpi-orders" />
        <Kpi icon={TrendingUp} label={t("dashboard.outstanding_value")} value={fmtCurrency(data.outstanding_value)} to="/quotes" testId="kpi-outstanding-value" accent="text-sky-500" />
      </div>

      {(data.finance || data.operations) && <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {data.finance && <Card className="p-5"><h3 className="mb-4 font-heading text-base font-semibold">{t("dashboard.next_open_invoices")}</h3>{data.recent_open_invoices.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">{t("dashboard.no_open_invoices")}</p> : <ul className="divide-y divide-slate-100 dark:divide-slate-800">{data.recent_open_invoices.map((invoice) => <li key={invoice.id}><Link to={`/finance/einvoices/${invoice.id}`} className="flex items-center justify-between gap-3 py-2.5 text-sm hover:text-primary"><span><b className="tabular-nums">{invoice.number}</b><span className="ml-2 text-slate-500">{invoice.customer_name}</span><div className={invoice.overdue ? "text-xs text-red-500" : "text-xs text-slate-400"}>{t("dashboard.due")}: {invoice.due_date ? fmtDate(invoice.due_date) : "—"}</div></span><b className="tabular-nums">{fmtCurrency(invoice.open_amount)}</b></Link></li>)}</ul>}</Card>}
        {data.operations && <Card className="p-5"><h3 className="mb-4 font-heading text-base font-semibold">{t("dashboard.today_schedule")}</h3>{data.today_appointments.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">{t("dashboard.no_appointments_today")}</p> : <ul className="divide-y divide-slate-100 dark:divide-slate-800">{data.today_appointments.map((job) => <li key={job.id}><Link to={`/jobs?open=${job.id}`} className="flex items-center justify-between gap-3 py-2.5 text-sm hover:text-primary"><span><b>{job.title}</b><div className="text-xs text-slate-400">{job.number} · {job.customer_name || "—"}</div></span><b className="tabular-nums">{new Date(job.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</b></Link></li>)}</ul>}</Card>}
      </div>}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="recent-quotes-card">
          <h3 className="mb-4 font-heading text-base font-semibold">{t("dashboard.recent_quotes")}</h3>
          {data.recent_quotes.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">{t("dashboard.empty_quotes")}</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.recent_quotes.map((q) => (
                <li key={q.id}>
                  <Link to={`/quotes/${q.id}`} className="flex items-center justify-between py-2.5 text-sm hover:text-primary" data-testid={`recent-quote-${q.id}`}>
                    <span className="flex items-center gap-3">
                      <span className="font-medium tabular-nums">{q.quote_number}</span>
                      <StatusBadge status={q.status} />
                    </span>
                    <span className="tabular-nums text-slate-600 dark:text-slate-400">{fmtCurrency(q.total)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5" data-testid="recent-customers-card">
          <h3 className="mb-4 font-heading text-base font-semibold">{t("dashboard.recent_customers")}</h3>
          {data.recent_customers.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">{t("dashboard.empty_customers")}</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.recent_customers.map((cu) => (
                <li key={cu.id}>
                  <Link to={`/customers/${cu.id}`} className="flex items-center justify-between py-2.5 text-sm hover:text-primary" data-testid={`recent-customer-${cu.id}`}>
                    <span className="font-medium">{cu.name}</span>
                    <span className="tabular-nums text-slate-400">{cu.customer_number}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
